import { useEffect, useState } from 'react';

import { Parameters } from '@provider-portal/common/api/gen/DeliveryProviderPortalAuthApi';
import { identity } from 'lodash';
import { Store } from 'redux';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { NetworkRequestState } from '@bolteu/redux-session';

const FEATURE_FLAG_EVENT_NAME = 'delivery_provider_portal_flags';

const hasTargetingParametersStateChanged = (
  prev: TargetingParametersState,
  next: TargetingParametersState,
): boolean =>
  prev !== next ||
  prev.parameters !== next.parameters ||
  prev.error !== next.error ||
  prev.fetchingState !== next.fetchingState ||
  prev.updateRequestState !== next.updateRequestState;

export class TargetingService {
  private readonly stateSubject: BehaviorSubject<TargetingParametersState>;

  constructor(private globalReduxStore: Store) {
    this.stateSubject = new BehaviorSubject<TargetingParametersState>(
      this.getTargetingParametersState(),
    );

    this.globalReduxStore.subscribe(() => {
      // Warning: this sub is never removed, but so long as we expect a single TargetingService instance,
      // the footprint will not be significant.
      const prevState = this.stateSubject.value;
      const nextState = this.getTargetingParametersState();

      if (hasTargetingParametersStateChanged(prevState, nextState)) {
        this.stateSubject.next(nextState);
      }
    });
  }

  public getTargetingParametersState(): TargetingParametersState {
    return this.globalReduxStore.getState().targetingParameters;
  }

  public isTargetingParametersRequestFinished(): boolean {
    const state = this.getTargetingParametersState();
    return (
      state.fetchingState === NetworkRequestState.success ||
      state.fetchingState === NetworkRequestState.failure
    );
  }

  public observeRequestFinished(): Observable<void> {
    return new Observable<void>((subscriber) => {
      if (this.isTargetingParametersRequestFinished()) {
        subscriber.next();
        subscriber.complete();
        return () => {};
      }

      const dispose = this.globalReduxStore.subscribe(() => {
        if (this.isTargetingParametersRequestFinished()) {
          subscriber.next();
          subscriber.complete();
          dispose();
        }
      });
      return () => dispose();
    });
  }

  public observeParameter<Q extends keyof Parameters, P extends keyof Parameters[Q]>(
    targetingEventName: Q,
    testOrFlagName: P,
  ): Observable<Parameters[Q][P] | undefined> {
    return this.stateSubject.asObservable().pipe(
      map(({ parameters }) => parameters?.[targetingEventName]?.[testOrFlagName]),
      distinctUntilChanged(),
    );
  }

  // Why not use TargetingService.getTargetingParametersState().parameters directly?
  // Why is fetching state: error not taken into account?
  public getParameters(): Parameters {
    return this.globalReduxStore.getState().targetingParameters.parameters;
  }
}

interface TargetingParametersState {
  parameters: Parameters | null;
  error?: Error;
  fetchingState: NetworkRequestState;
  updateRequestState: NetworkRequestState;
}

// @review why not use const { targetingService } = useCommonAppServices(); inside hook directly?
// @todo identity function does not handle undefined properly
export const useTargetingParameter = <
  P extends keyof Parameters[Q],
  Q extends keyof Parameters = typeof FEATURE_FLAG_EVENT_NAME,
  V = Required<Parameters[Q][P]>,
>(
  testOrFlagName: P,
  targetingService: TargetingService,
  valueAdapter: (v: V | undefined) => V = identity,
  targetingEventName: Q = FEATURE_FLAG_EVENT_NAME as Q,
): V => {
  const [value, setValue] = useState<V>(valueAdapter(undefined));

  useEffect(() => {
    const sub = targetingService
      .observeParameter(targetingEventName, testOrFlagName)
      .subscribe((p) => setValue(valueAdapter(p)));
    return () => sub.unsubscribe();
  }, [targetingService, testOrFlagName, targetingEventName, valueAdapter]);

  return value;
};
